import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Chip,
  Tooltip,
  Typography,
} from "@mui/material";

import AssessmentIcon from "@mui/icons-material/Assessment";
import AddIcon from "@mui/icons-material/Add";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import PostAddIcon from "@mui/icons-material/PostAdd";
import VerifiedIcon from "@mui/icons-material/Verified";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AnexarLicencaModal from "../../components/AnexarLicencaModal";
import CriarPublicacaoPedidoModal from "../../components/CriarPublicacaoPedidoModal";
import IsencaoAnalistaModal from "../../components/IsencaoAnalistaModal";
import PublicacaoAnalistaModal from "../../components/PublicacaoAnalistaModal";
import ProtocoloAnalistaModal from "../../components/ProtocoloAnalistaModal";
import AutorizacaoStatusChip from "../../components/AutorizacaoStatusChip";
import StatusChip from "../../components/StatusChip";
import licencasService from "../../services/licencas";
import licencaEmitidaService from "../../services/licencaEmitida";
import autorizacoesService from "../../services/autorizacoes";
import isencaoLicenciamentoConsultaService from "../../services/isencaoLicenciamentoConsulta";
import cdnslamService from "../../services/cdnslam";
import publicacaoService from "../../services/publicacao";
import protocoloService from "../../services/protocolo";
import usuarioService from "../../services/usuario";
import { statusLabels, statusLabelsEnum, tipoAutorizacaoEnum, statusIsencaoLabels, statusPublicacaoLabels } from "../../utils/constants";
import { protocoloStatusLabels, getAtividadeFlorestalResumo, PROTOCOLO_STATUS_EMITIDO } from "../../utils/protocoloConstants";
import {
  getIsencaoPaletteKey,
  getMuiChipColor,
  getProtocoloPaletteKey,
  getPublicacaoPaletteKey,
  getStatusPaletteKey,
} from "../../utils/statusPalette";
import { formatCpfCnpj, formatDateToShow } from "../../utils/formatters";
import { baixarRequerimentoProtocoloPdf } from "../../utils/requerimentoProtocoloPdf";

const DASHBOARD_TABLE_MIN_WIDTH = 960;

const statusProgressEntries = Object.entries(statusLabels);

const buildStatusCounts = (solicitacoes) => {
  const counts = {};
  statusProgressEntries.forEach(([statusKey]) => {
    counts[statusKey] = solicitacoes.filter(
      (s) => s.status === Number(statusKey),
    ).length;
  });
  return counts;
};

const renderBarraProgressoStatus = (statusKey, label, count, total) => {
  const paletteKey = getStatusPaletteKey(Number(statusKey));
  const percent = total ? Math.round((count / total) * 100) : 0;

  return (
    <Box mt={2} key={statusKey}>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" color="text.secondary">
          {count} ({percent}%)
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={total ? (count / total) * 100 : 0}
        sx={{
          height: 10,
          borderRadius: 5,
          mt: 0.5,
          backgroundColor: "action.hover",
          "& .MuiLinearProgress-bar": {
            bgcolor: paletteKey === "grey" ? "grey.500" : `${paletteKey}.main`,
          },
        }}
      />
    </Box>
  );
};

const AnalystDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, counts: {} });
  // eslint-disable-next-line no-unused-vars
  const [processos, setProcessos] = useState([]);
  const [recentProcessos, setRecentProcessos] = useState([]);
  const [user] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [anexarLicencaModalOpen, setAnexarLicencaModalOpen] = useState(false);
  const [processoParaAnexar, setProcessoParaAnexar] = useState(null);

  // Autorizações
  const [autorizacoes, setAutorizacoes] = useState([]);
  const [loadingAutorizacoes, setLoadingAutorizacoes] = useState(false);
  const [pageAuth, setPageAuth] = useState(0);
  const [rowsPerPageAuth, setRowsPerPageAuth] = useState(5);
  const [totalAutorizacoes, setTotalAutorizacoes] = useState(0);

  // Isenções
  const [isencoes, setIsencoes] = useState([]);
  const [loadingIsencoes, setLoadingIsencoes] = useState(false);
  const [pageIsencao, setPageIsencao] = useState(0);
  const [rowsPerPageIsencao, setRowsPerPageIsencao] = useState(5);
  const [totalIsencoes, setTotalIsencoes] = useState(0);
  const [isencaoSelecionada, setIsencaoSelecionada] = useState(null);
  const [isencaoModalAberto, setIsencaoModalAberto] = useState(false);

  // CDNSLAM
  const [cdnslams, setCdnslams] = useState([]);
  const [loadingCdnslam, setLoadingCdnslam] = useState(false);
  const [pageCdnslam, setPageCdnslam] = useState(0);
  const [rowsPerPageCdnslam, setRowsPerPageCdnslam] = useState(5);
  const [totalCdnslam, setTotalCdnslam] = useState(0);
  const [downloadingCdnslamId, setDownloadingCdnslamId] = useState(null);

  const [publicacoes, setPublicacoes] = useState([]);
  const [loadingPublicacoes, setLoadingPublicacoes] = useState(false);
  const [pagePublicacao, setPagePublicacao] = useState(0);
  const [rowsPerPagePublicacao, setRowsPerPagePublicacao] = useState(5);
  const [totalPublicacoes, setTotalPublicacoes] = useState(0);
  const [publicacaoSelecionada, setPublicacaoSelecionada] = useState(null);
  const [publicacaoModalAberto, setPublicacaoModalAberto] = useState(false);

  const [pedidosPublicacao, setPedidosPublicacao] = useState([]);
  const [loadingPedidosPublicacao, setLoadingPedidosPublicacao] = useState(false);
  const [pagePedidoPublicacao, setPagePedidoPublicacao] = useState(0);
  const [rowsPerPagePedidoPublicacao, setRowsPerPagePedidoPublicacao] = useState(5);
  const [totalPedidosPublicacao, setTotalPedidosPublicacao] = useState(0);
  const [criarPedidoModalAberto, setCriarPedidoModalAberto] = useState(false);
  const [baixandoComprovantePedidoId, setBaixandoComprovantePedidoId] = useState(null);

  // Protocolos
  const [protocolos, setProtocolos] = useState([]);
  const [loadingProtocolos, setLoadingProtocolos] = useState(false);
  const [pageProtocolo, setPageProtocolo] = useState(0);
  const [rowsPerPageProtocolo, setRowsPerPageProtocolo] = useState(5);
  const [totalProtocolos, setTotalProtocolos] = useState(0);
  const [protocoloSelecionado, setProtocoloSelecionado] = useState(null);
  const [protocoloModalAberto, setProtocoloModalAberto] = useState(false);
  const [downloadingProtocoloId, setDownloadingProtocoloId] = useState(null);

  const handleDownloadProtocoloRequerimento = async (protocolo) => {
    if (protocolo.status !== PROTOCOLO_STATUS_EMITIDO) return;
    setDownloadingProtocoloId(protocolo.id);
    try {
      const detalhes = await protocoloService.obterPorId(protocolo.id);
      let requerente = null;
      if (detalhes?.createUserId) {
        try {
          requerente = await usuarioService.obterUsuarioPorId(detalhes.createUserId);
        } catch {
          requerente = null;
        }
      }
      await baixarRequerimentoProtocoloPdf(detalhes, requerente);
    } catch {
      alert("Erro ao baixar o requerimento. Tente novamente.");
    } finally {
      setDownloadingProtocoloId(null);
    }
  };
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  const carregarProcessos = async () => {
    try {
      setLoading(true);
      const response = await licencasService.listarSolicitacoesAnalista();

      const calculatedStats = {
        total: response.length,
        counts: buildStatusCounts(response),
      };
      setStats(calculatedStats);

      const sortedProcessos = [...response].sort(
        (a, b) => new Date(b.createAt) - new Date(a.createAt),
      );
      setProcessos(sortedProcessos);
      setRecentProcessos(sortedProcessos);
    } catch (error) {
      console.error("Erro ao carregar processos:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    carregarProcessos();
  }, []);

  useEffect(() => {
    carregarAutorizacoes(pageAuth, rowsPerPageAuth);
    carregarIsencoes(pageIsencao, rowsPerPageIsencao);
    carregarCdnslams(pageCdnslam, rowsPerPageCdnslam);
    carregarPublicacoes(pagePublicacao, rowsPerPagePublicacao);
    carregarPedidosPublicacao(pagePedidoPublicacao, rowsPerPagePedidoPublicacao);
    carregarProtocolos(pageProtocolo, rowsPerPageProtocolo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleAbrirDetalheSolicitacao = (processo) => {
    navigate(`/analista/solicitacao/${processo.id}`);
  };
  const handleFecharAnexarLicencaModal = () => {
    setAnexarLicencaModalOpen(false);
    setProcessoParaAnexar(null);
  };

  const carregarAutorizacoes = async (pag = 0, rpp = 5) => {
    try {
      setLoadingAutorizacoes(true);
      const result = await autorizacoesService.listarTodasAnalista({
        pageNumber: pag + 1,
        pageSize: rpp,
      });
      setAutorizacoes(result.items);
      setTotalAutorizacoes(result.totalCount);
    } catch (error) {
      console.error("Erro ao carregar autorizações:", error);
    } finally {
      setLoadingAutorizacoes(false);
    }
  };

  const handleChangePageAuth = (event, newPage) => {
    setPageAuth(newPage);
    carregarAutorizacoes(newPage, rowsPerPageAuth);
  };

  const handleChangeRowsPerPageAuth = (event) => {
    const rpp = parseInt(event.target.value, 10);
    setRowsPerPageAuth(rpp);
    setPageAuth(0);
    carregarAutorizacoes(0, rpp);
  };

  const carregarIsencoes = async (pag = 0, rpp = 5) => {
    try {
      setLoadingIsencoes(true);
      const result = await isencaoLicenciamentoConsultaService.listarTodasAnalista({
        pageNumber: pag + 1,
        pageSize: rpp,
      });
      setIsencoes(result?.data?.data ?? []);
      setTotalIsencoes(result?.data?.totalCount ?? 0);
    } catch (error) {
      console.error("Erro ao carregar isenções:", error);
    } finally {
      setLoadingIsencoes(false);
    }
  };

  const handleChangePageIsencao = (event, newPage) => {
    setPageIsencao(newPage);
    carregarIsencoes(newPage, rowsPerPageIsencao);
  };

  const handleChangeRowsPerPageIsencao = (event) => {
    const rpp = parseInt(event.target.value, 10);
    setRowsPerPageIsencao(rpp);
    setPageIsencao(0);
    carregarIsencoes(0, rpp);
  };

  const carregarCdnslams = async (pag = 0, rpp = 5) => {
    try {
      setLoadingCdnslam(true);
      const result = await cdnslamService.listarTodasAnalista({
        pageNumber: pag + 1,
        pageSize: rpp,
      });
      setCdnslams(result?.data?.data ?? []);
      setTotalCdnslam(result?.data?.totalCount ?? 0);
    } catch (error) {
      console.error("Erro ao carregar CDNSLAM:", error);
    } finally {
      setLoadingCdnslam(false);
    }
  };

  const handleChangePageCdnslam = (event, newPage) => {
    setPageCdnslam(newPage);
    carregarCdnslams(newPage, rowsPerPageCdnslam);
  };

  const handleChangeRowsPerPageCdnslam = (event) => {
    const rpp = parseInt(event.target.value, 10);
    setRowsPerPageCdnslam(rpp);
    setPageCdnslam(0);
    carregarCdnslams(0, rpp);
  };

  const handleDownloadCdnslam = async (id) => {
    setDownloadingCdnslamId(id);
    try {
      const response = await cdnslamService.obterPorId(id);
      const cdnslam = response?.data;
      if (cdnslam?.fileContent) {
        const byteArray = Uint8Array.from(atob(cdnslam.fileContent), (c) => c.charCodeAt(0));
        const blob = new Blob([byteArray], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = cdnslam.fileName || "CDNSLAM.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        setSnackbarMessage("Arquivo da CDNSLAM não encontrado.");
        setSnackbarOpen(true);
      }
    } catch {
      setSnackbarMessage("Erro ao baixar CDNSLAM.");
      setSnackbarOpen(true);
    } finally {
      setDownloadingCdnslamId(null);
    }
  };

  const carregarPublicacoes = async (pag = 0, rpp = 5) => {
    try {
      setLoadingPublicacoes(true);
      const result = await publicacaoService.listarPendentesAnalista(pag + 1, rpp);
      setPublicacoes(result?.data ?? []);
      setTotalPublicacoes(result?.totalCount ?? 0);
    } catch (error) {
      console.error("Erro ao carregar publicações:", error);
    } finally {
      setLoadingPublicacoes(false);
    }
  };

  const handleChangePagePublicacao = (event, newPage) => {
    setPagePublicacao(newPage);
    carregarPublicacoes(newPage, rowsPerPagePublicacao);
  };

  const handleChangeRowsPerPagePublicacao = (event) => {
    const rpp = parseInt(event.target.value, 10);
    setRowsPerPagePublicacao(rpp);
    setPagePublicacao(0);
    carregarPublicacoes(0, rpp);
  };

  const carregarPedidosPublicacao = async (pag = 0, rpp = 5) => {
    try {
      setLoadingPedidosPublicacao(true);
      const result = await publicacaoService.listarPedidos({
        pageNumber: pag + 1,
        pageSize: rpp,
      });
      setPedidosPublicacao(result?.data ?? []);
      setTotalPedidosPublicacao(result?.totalCount ?? 0);
    } catch (error) {
      console.error("Erro ao carregar pedidos de publicação:", error);
    } finally {
      setLoadingPedidosPublicacao(false);
    }
  };

  const handleChangePagePedidoPublicacao = (event, newPage) => {
    setPagePedidoPublicacao(newPage);
    carregarPedidosPublicacao(newPage, rowsPerPagePedidoPublicacao);
  };

  const handleChangeRowsPerPagePedidoPublicacao = (event) => {
    const rpp = parseInt(event.target.value, 10);
    setRowsPerPagePedidoPublicacao(rpp);
    setPagePedidoPublicacao(0);
    carregarPedidosPublicacao(0, rpp);
  };

  const handleBaixarComprovantePedido = async (pedido) => {
    setBaixandoComprovantePedidoId(pedido.id);
    try {
      const blob = await publicacaoService.baixarComprovante(pedido.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `comprovante-pedido-licenca-${pedido.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro ao baixar comprovante do pedido:", error);
      setSnackbarMessage("Erro ao baixar comprovante do pedido.");
      setSnackbarOpen(true);
    } finally {
      setBaixandoComprovantePedidoId(null);
    }
  };

  const carregarProtocolos = async (pag = 0, rpp = 5) => {
    try {
      setLoadingProtocolos(true);
      const result = await protocoloService.listarTodasAnalista(pag + 1, rpp);
      setProtocolos(result?.data ?? []);
      setTotalProtocolos(result?.totalCount ?? 0);
    } catch (error) {
      console.error("Erro ao carregar protocolos:", error);
      setProtocolos([]);
      setTotalProtocolos(0);
    } finally {
      setLoadingProtocolos(false);
    }
  };

  const handleChangePageProtocolo = (event, newPage) => {
    setPageProtocolo(newPage);
    carregarProtocolos(newPage, rowsPerPageProtocolo);
  };

  const handleChangeRowsPerPageProtocolo = (event) => {
    const rpp = parseInt(event.target.value, 10);
    setRowsPerPageProtocolo(rpp);
    setPageProtocolo(0);
    carregarProtocolos(0, rpp);
  };
  const handleVisualizarLicencaAssinada = async (solicitacaoId) => {
    try {
      const response = await licencaEmitidaService.obterPorSolicitacaoDeLicencaId(solicitacaoId);
      const licencaData = response.data;

      let blob;
      if (typeof licencaData.fileContent === "string") {
        const byteCharacters = atob(licencaData.fileContent);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        blob = new Blob([new Uint8Array(byteNumbers)], {
          type: licencaData.contextType || "application/pdf",
        });
      } else {
        blob = new Blob([new Uint8Array(licencaData.fileContent)], {
          type: licencaData.contextType || "application/pdf",
        });
      }

      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (error) {
      alert("Erro ao carregar a licença assinada.");
    }
  };

  const handleSubmitAnexoLicenca = async (file, processo) => {
    try {
      await licencaEmitidaService.criarLicencaEmitida(processo.id, file);
      setSnackbarMessage("Licença anexada com sucesso!");
      setSnackbarOpen(true);
      carregarProcessos();
    } catch (error) {
      console.error("Erro ao anexar licença:", error);
      const fluentErrors = error.response?.data?.errors;
      const mensagemErro =
        error.response?.data?.message ||
        (fluentErrors
          ? Object.values(fluentErrors).flat().join(" ")
          : null) ||
        "Erro ao anexar licença. Tente novamente.";
      throw new Error(mensagemErro);
    }
  };
  if (loading) {
    return (
      <Container sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 2, sm: 3, md: 4 } }}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="50vh"
        >
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Carregando seu painel de análise...
          </Typography>
        </Box>
      </Container>
    );
  }
  return (
    <Container sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 2, sm: 3, md: 4 } }}>
      <Box
        mb={{ xs: 3, md: 4 }}
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        gap={2}
      >
        <Box>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ fontSize: { xs: "1.35rem", sm: "1.75rem", md: "2.125rem" } }}
          >
            Painel do Analista - {user?.username || "Analista"}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gerencie os processos de licenciamento ambiental sob sua
            responsabilidade
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={carregarProcessos}
          fullWidth
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          Atualizar
        </Button>
      </Box>
      <Box mb={4}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            border: 1,
            borderColor: "divider",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Progresso de Análise</Typography>
            <AssessmentIcon color="info" />
          </Box>
          <Box mt={3}>
            <Typography variant="body1" gutterBottom>
              Distribuição de status das solicitações ({stats.total} total)
            </Typography>
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} md={6}>
                {statusProgressEntries
                  .slice(0, Math.ceil(statusProgressEntries.length / 2))
                  .map(([statusKey, label]) =>
                    renderBarraProgressoStatus(
                      statusKey,
                      label,
                      stats.counts[statusKey] ?? 0,
                      stats.total,
                    ),
                  )}
              </Grid>
              <Grid item xs={12} md={6}>
                {statusProgressEntries
                  .slice(Math.ceil(statusProgressEntries.length / 2))
                  .map(([statusKey, label]) =>
                    renderBarraProgressoStatus(
                      statusKey,
                      label,
                      stats.counts[statusKey] ?? 0,
                      stats.total,
                    ),
                  )}
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
      <Box mb={4}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h5" sx={{ fontSize: { xs: "1.1rem", md: "1.5rem" } }}>
            Solicitações de Licença Pendentes
          </Typography>
        </Box>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: 1,
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <TableContainer sx={{ overflowX: "auto", width: "100%" }}>
            <Table sx={{ minWidth: DASHBOARD_TABLE_MIN_WIDTH }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell align="center">Tipo</TableCell>
                  <TableCell align="center">Empreendimento</TableCell>
                  <TableCell align="center">Solicitante</TableCell>
                  <TableCell align="center">Data Inicial</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentProcessos
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((processo) => (
                    <TableRow
                      key={processo.id}
                      hover
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        cursor: "pointer",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <TableCell>
                        {processo.nomeTipoLicenca || "Não especificado"}
                      </TableCell>
                      <TableCell>
                        {processo.empreendimento?.nome || "—"}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Avatar
                            sx={{
                              width: 24,
                              height: 24,
                              mr: 1,
                              bgcolor: "info.main",
                              fontSize: "0.8rem",
                            }}
                          >
                            {(() => {
                              const primeiraLetra =
                                processo.solicitante_info?.first_name?.[0]?.toUpperCase() ||
                                processo.licenca?.usuario?.first_name?.[0]?.toUpperCase() ||
                                processo.usuario?.first_name?.[0]?.toUpperCase() ||
                                processo.solicitante_info?.username?.[0]?.toUpperCase() ||
                                processo.licenca?.usuario?.username?.[0]?.toUpperCase() ||
                                processo.usuario?.username?.[0]?.toUpperCase() ||
                                "U";
                              return primeiraLetra;
                            })()}
                          </Avatar>
                          <Typography variant="body2">
                            {processo.solicitanteNome || "Não especificado"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {formatDateToShow(processo.createAt)}
                      </TableCell>
                      <TableCell align="center">
                        <StatusChip status={processo.status} />
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            justifyContent: "flex-end",
                          }}
                        >
                          {processo.status === statusLabelsEnum.APROVADA && (
                            <Tooltip title="Emitir Licença">
                              <IconButton
                                size="small"
                                color="success"
                                aria-label="Emitir Licença"
                                onClick={() =>
                                  navigate(`/solicitacao/${processo.id}/emitir`)
                                }
                              >
                                <DescriptionIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {(processo.status === statusLabelsEnum.LICENCA_EMITIDA ||
                            processo.status === statusLabelsEnum.LICENCA_ASSINADA) && (
                            <Tooltip title="Regenerar Licença">
                              <IconButton
                                size="small"
                                color="warning"
                                aria-label="Regenerar Licença"
                                onClick={() =>
                                  navigate(`/solicitacao/${processo.id}/regenerar`)
                                }
                              >
                                <RefreshIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {processo.status === statusLabelsEnum.LICENCA_ASSINADA && (
                            <Tooltip title="Visualizar Licença Assinada">
                              <IconButton
                                size="small"
                                color="info"
                                aria-label="Visualizar Licença Assinada"
                                onClick={() =>
                                  handleVisualizarLicencaAssinada(processo.id)
                                }
                              >
                                <VerifiedIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Visualizar Documentos">
                            <IconButton
                              size="small"
                              color="info"
                              aria-label="Visualizar Documentos"
                              onClick={() => handleAbrirDetalheSolicitacao(processo)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                {recentProcessos.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        Nenhum processo encontrado
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider />
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={processos.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Itens por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
          />
        </Paper>
      </Box>

      <Box mb={4}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h5" sx={{ fontSize: { xs: "1.1rem", md: "1.5rem" } }}>Autorizações Pendentes</Typography>
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              size="small"
              startIcon={<PostAddIcon />}
              onClick={() => navigate("/analista/autorizacao-independente")}
            >
              Nova Autorização Independente
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => carregarAutorizacoes(pageAuth, rowsPerPageAuth)}
            >
              Atualizar
            </Button>
          </Box>
        </Box>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: 1,
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <TableContainer sx={{ overflowX: "auto", width: "100%" }}>
            <Table sx={{ minWidth: DASHBOARD_TABLE_MIN_WIDTH }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell align="center">Tipo</TableCell>
                  <TableCell align="center">Nome do Evento</TableCell>
                  <TableCell align="center">Data do Evento</TableCell>
                  <TableCell align="center">Local</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Data da Solicitação</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingAutorizacoes ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : autorizacoes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        Nenhuma autorização encontrada
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  autorizacoes.map((aut) => (
                    <TableRow
                      key={aut.id}
                      hover
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        cursor: "pointer",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <TableCell align="center">
                        {tipoAutorizacaoEnum.find((t) => t.value === aut.tipoAutorizacao)?.label ?? "—"}
                      </TableCell>
                      <TableCell>{aut.nomeEvento}</TableCell>
                      <TableCell align="center">
                        {formatDateToShow(aut.dataEvento)}
                      </TableCell>
                      <TableCell>{aut.localEvento}</TableCell>
                      <TableCell align="center">
                        <AutorizacaoStatusChip status={aut.status} />
                      </TableCell>
                      <TableCell align="center">
                        {formatDateToShow(aut.createAt)}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ver Detalhes">
                          <IconButton
                            size="small"
                            color="info"
                            aria-label="Ver Detalhes"
                            onClick={() => navigate(`/autorizacao/${aut.id}`)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider />
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalAutorizacoes}
            rowsPerPage={rowsPerPageAuth}
            page={pageAuth}
            onPageChange={handleChangePageAuth}
            onRowsPerPageChange={handleChangeRowsPerPageAuth}
            labelRowsPerPage="Itens por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
          />
        </Paper>
      </Box>

      {/* Isenções de Licenciamento */}
      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" sx={{ fontSize: { xs: "1.1rem", md: "1.5rem" } }}>Isenções de Licenciamento</Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => carregarIsencoes(pageIsencao, rowsPerPageIsencao)}
          >
            Atualizar
          </Button>
        </Box>
        <Paper elevation={0} sx={{ borderRadius: 2, border: 1, borderColor: "divider", overflow: "hidden" }}>
          <TableContainer sx={{ overflowX: "auto", width: "100%" }}>
            <Table sx={{ minWidth: DASHBOARD_TABLE_MIN_WIDTH }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell>Empreendimento</TableCell>
                  <TableCell align="center">Data Solicitação</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingIsencoes ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : isencoes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        Nenhuma isenção encontrada
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  isencoes.map((isencao) => (
                    <TableRow
                      key={isencao.id}
                      hover
                      sx={{ "&:last-child td, &:last-child th": { border: 0 }, cursor: "pointer" }}
                    >
                      <TableCell>{isencao.empreendimentoNome || "—"}</TableCell>
                      <TableCell align="center">{formatDateToShow(isencao.createAt)}</TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={statusIsencaoLabels[isencao.status] ?? isencao.status}
                          color={getMuiChipColor(getIsencaoPaletteKey(isencao.status))}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ver Detalhes">
                          <IconButton
                            size="small"
                            color="info"
                            aria-label="Ver Detalhes"
                            onClick={() => {
                              setIsencaoSelecionada(isencao);
                              setIsencaoModalAberto(true);
                            }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider />
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalIsencoes}
            rowsPerPage={rowsPerPageIsencao}
            page={pageIsencao}
            onPageChange={handleChangePageIsencao}
            onRowsPerPageChange={handleChangeRowsPerPageIsencao}
            labelRowsPerPage="Itens por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </Paper>
      </Box>

      {/* CDNSLAM */}
      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" sx={{ fontSize: { xs: "1.1rem", md: "1.5rem" } }}>
            CDNSLAM
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => carregarCdnslams(pageCdnslam, rowsPerPageCdnslam)}
          >
            Atualizar
          </Button>
        </Box>
        <Paper elevation={0} sx={{ borderRadius: 2, border: 1, borderColor: "divider", overflow: "hidden" }}>
          <TableContainer sx={{ overflowX: "auto", width: "100%" }}>
            <Table sx={{ minWidth: DASHBOARD_TABLE_MIN_WIDTH }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell>Empreendimento</TableCell>
                  <TableCell>Solicitante</TableCell>
                  <TableCell>Número do Processo</TableCell>
                  <TableCell align="center">Data de Emissão</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingCdnslam ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : cdnslams.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        Nenhuma CDNSLAM encontrada
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  cdnslams.map((cdnslam) => (
                    <TableRow
                      key={cdnslam.id}
                      hover
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell>{cdnslam.empreendimentoNome || "—"}</TableCell>
                      <TableCell>{cdnslam.solicitanteNome || "—"}</TableCell>
                      <TableCell>{cdnslam.numeroProcesso || "—"}</TableCell>
                      <TableCell align="center">{formatDateToShow(cdnslam.createAt)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Baixar CDNSLAM">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              aria-label="Baixar CDNSLAM"
                              disabled={!!downloadingCdnslamId}
                              onClick={() => handleDownloadCdnslam(cdnslam.id)}
                            >
                              {downloadingCdnslamId === cdnslam.id ? (
                                <CircularProgress size={18} />
                              ) : (
                                <DownloadIcon />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider />
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalCdnslam}
            rowsPerPage={rowsPerPageCdnslam}
            page={pageCdnslam}
            onPageChange={handleChangePageCdnslam}
            onRowsPerPageChange={handleChangeRowsPerPageCdnslam}
            labelRowsPerPage="Itens por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </Paper>
      </Box>

      {/* Publicações no site */}
      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" sx={{ fontSize: { xs: "1.1rem", md: "1.5rem" } }}>
            Publicações no site
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => carregarPublicacoes(pagePublicacao, rowsPerPagePublicacao)}
          >
            Atualizar
          </Button>
        </Box>
        <Paper elevation={0} sx={{ borderRadius: 2, border: 1, borderColor: "divider", overflow: "hidden" }}>
          <TableContainer sx={{ overflowX: "auto", width: "100%" }}>
            <Table sx={{ minWidth: DASHBOARD_TABLE_MIN_WIDTH }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell>Empreendimento</TableCell>
                  <TableCell>Tipo de processo</TableCell>
                  <TableCell align="center">Data</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingPublicacoes ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : publicacoes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        Nenhuma publicação pendente
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  publicacoes.map((publicacao) => (
                    <TableRow key={publicacao.id} hover>
                      <TableCell>{publicacao.empreendimentoNome || "—"}</TableCell>
                      <TableCell>{publicacao.tipoLicencaNome || "—"}</TableCell>
                      <TableCell align="center">
                        {formatDateToShow(publicacao.createAt)}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={statusPublicacaoLabels[publicacao.status] ?? publicacao.status}
                          color={getMuiChipColor(getPublicacaoPaletteKey(publicacao.status))}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ver Detalhes">
                          <IconButton
                            size="small"
                            color="info"
                            aria-label="Ver Detalhes"
                            onClick={() => {
                              setPublicacaoSelecionada(publicacao);
                              setPublicacaoModalAberto(true);
                            }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider />
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalPublicacoes}
            rowsPerPage={rowsPerPagePublicacao}
            page={pagePublicacao}
            onPageChange={handleChangePagePublicacao}
            onRowsPerPageChange={handleChangeRowsPerPagePublicacao}
            labelRowsPerPage="Itens por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </Paper>
      </Box>

      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
          <Typography variant="h5" sx={{ fontSize: { xs: "1.1rem", md: "1.5rem" } }}>
            Pedidos de publicação
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setCriarPedidoModalAberto(true)}
            >
              Nova publicação — Pedido de licença
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() =>
                carregarPedidosPublicacao(pagePedidoPublicacao, rowsPerPagePedidoPublicacao)
              }
            >
              Atualizar
            </Button>
          </Box>
        </Box>
        <Paper elevation={0} sx={{ borderRadius: 2, border: 1, borderColor: "divider", overflow: "hidden" }}>
          <TableContainer sx={{ overflowX: "auto", width: "100%" }}>
            <Table sx={{ minWidth: DASHBOARD_TABLE_MIN_WIDTH }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell>Nome / Razão Social</TableCell>
                  <TableCell>CPF/CNPJ</TableCell>
                  <TableCell>Tipo de licença</TableCell>
                  <TableCell align="center">Data</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingPedidosPublicacao ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : pedidosPublicacao.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        Nenhum pedido de publicação encontrado
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  pedidosPublicacao.map((pedido) => (
                    <TableRow key={pedido.id} hover>
                      <TableCell>{pedido.nomeOuRazaoSocial || "—"}</TableCell>
                      <TableCell>
                        {pedido.cnpj ? formatCpfCnpj(pedido.cnpj) : "—"}
                      </TableCell>
                      <TableCell>{pedido.tipoLicencaNome || "—"}</TableCell>
                      <TableCell align="center">
                        {formatDateToShow(pedido.createAt)}
                      </TableCell>
                      <TableCell align="center">
                        {pedido.temComprovantePdf && (
                          <Tooltip title="Baixar comprovante">
                            <IconButton
                              size="small"
                              color="success"
                              aria-label="Baixar comprovante"
                              disabled={baixandoComprovantePedidoId === pedido.id}
                              onClick={() => handleBaixarComprovantePedido(pedido)}
                            >
                              {baixandoComprovantePedidoId === pedido.id ? (
                                <CircularProgress size={18} />
                              ) : (
                                <DownloadIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider />
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalPedidosPublicacao}
            rowsPerPage={rowsPerPagePedidoPublicacao}
            page={pagePedidoPublicacao}
            onPageChange={handleChangePagePedidoPublicacao}
            onRowsPerPageChange={handleChangeRowsPerPagePedidoPublicacao}
            labelRowsPerPage="Itens por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </Paper>
      </Box>

      {/* Protocolos */}
      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" sx={{ fontSize: { xs: "1.1rem", md: "1.5rem" } }}>Protocolos</Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => carregarProtocolos(pageProtocolo, rowsPerPageProtocolo)}
          >
            Atualizar
          </Button>
        </Box>
        <Paper elevation={0} sx={{ borderRadius: 2, border: 1, borderColor: "divider", overflow: "hidden" }}>
          <TableContainer sx={{ overflowX: "auto", width: "100%" }}>
            <Table sx={{ minWidth: DASHBOARD_TABLE_MIN_WIDTH }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell>Nº Processo</TableCell>
                  <TableCell>Tipo de Processo</TableCell>
                  <TableCell>Atividade Florestal</TableCell>
                  <TableCell align="center">Data</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingProtocolos ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : protocolos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        Nenhum protocolo encontrado
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  protocolos.map((protocolo) => (
                    <TableRow
                      key={protocolo.id}
                      hover
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell>{protocolo.numeroProcesso || "—"}</TableCell>
                      <TableCell>{protocolo.processoNome || "—"}</TableCell>
                      <TableCell>{getAtividadeFlorestalResumo(protocolo.atividadeEmpreendimento)}</TableCell>
                      <TableCell align="center">{formatDateToShow(protocolo.createAt)}</TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={protocoloStatusLabels[protocolo.status] ?? protocolo.status}
                          color={getMuiChipColor(getProtocoloPaletteKey(protocolo.status))}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                          {protocolo.status === PROTOCOLO_STATUS_EMITIDO && (
                            <Tooltip title="Baixar REQUERIMENTO">
                              <IconButton
                                size="small"
                                color="success"
                                aria-label="Baixar REQUERIMENTO"
                                disabled={downloadingProtocoloId === protocolo.id}
                                onClick={() => handleDownloadProtocoloRequerimento(protocolo)}
                              >
                                {downloadingProtocoloId === protocolo.id ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <DescriptionIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Ver Detalhes">
                            <IconButton
                              size="small"
                              color="info"
                              aria-label="Ver Detalhes"
                              onClick={() => {
                                setProtocoloSelecionado(protocolo);
                                setProtocoloModalAberto(true);
                              }}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider />
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalProtocolos}
            rowsPerPage={rowsPerPageProtocolo}
            page={pageProtocolo}
            onPageChange={handleChangePageProtocolo}
            onRowsPerPageChange={handleChangeRowsPerPageProtocolo}
            labelRowsPerPage="Itens por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </Paper>
      </Box>

      <IsencaoAnalistaModal
        open={isencaoModalAberto}
        onClose={() => setIsencaoModalAberto(false)}
        isencao={isencaoSelecionada}
        onStatusAtualizado={() => {
          setIsencaoModalAberto(false);
          carregarIsencoes(pageIsencao, rowsPerPageIsencao);
        }}
      />

      <PublicacaoAnalistaModal
        open={publicacaoModalAberto}
        onClose={() => setPublicacaoModalAberto(false)}
        publicacao={publicacaoSelecionada}
        onAtualizado={() => {
          carregarPublicacoes(pagePublicacao, rowsPerPagePublicacao);
        }}
      />

      <CriarPublicacaoPedidoModal
        open={criarPedidoModalAberto}
        onClose={() => setCriarPedidoModalAberto(false)}
        onSucesso={() => {
          carregarPedidosPublicacao(pagePedidoPublicacao, rowsPerPagePedidoPublicacao);
        }}
      />

      <ProtocoloAnalistaModal
        open={protocoloModalAberto}
        onClose={() => setProtocoloModalAberto(false)}
        protocolo={protocoloSelecionado}
        onAtualizado={() => carregarProtocolos(pageProtocolo, rowsPerPageProtocolo)}
      />

      <AnexarLicencaModal
        open={anexarLicencaModalOpen}
        onClose={handleFecharAnexarLicencaModal}
        onSubmit={handleSubmitAnexoLicenca}
        processo={processoParaAnexar}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Container>
  );
};
export default AnalystDashboard;
